import { useState, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Pencil, Trash2, Eye, Search, FileText, Upload, X, GripVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTenant } from "@/contexts/TenantContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IconPicker } from "@/components/categories/IconPicker";
import ProductCategoryDetails from "@/pages/ProductCategoryDetails";
import { CategoryFilters, CategoryFilterState } from "@/components/categories/CategoryFilters";
import { ExpenseCategoriesTab } from "@/components/categories/ExpenseCategoriesTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, Receipt } from "lucide-react";
import * as Icons from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { logCrud } from "@/services/activities";

type Category = {
  id: string;
  nome: string;
  foto_url: string | null;
  descricao: string | null;
  form_id: string | null;
  cor_hex: string | null;
  status: string;
  icone: string | null;
  ordem: number;
  created_at?: string | null;
  forms?: {
    nome: string;
  } | null;
};


export default function Categories() {
  const navigate = useNavigate();
  const [categoryProfile, setCategoryProfile] = useState<{ id: string; name: string } | null>(null);
  const [open, setOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const [filters, setFilters] = useState<CategoryFilterState>({
    searchTerm: "",
    status: [],
  });
  const [selectedIcon, setSelectedIcon] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("#000000");
  const [selectedFormId, setSelectedFormId] = useState<string>("__none__");

  // Sensors para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Buscar categorias
  const { data: categories, isLoading } = useQuery({
    queryKey: ["product_categories", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_categories")
        .select("*, forms(nome)")
        .eq("tenant_id", tenantId)
        .order("ordem", { ascending: true })
        .order("nome", { ascending: true });
      
      if (error) throw error;
      return (data || []) as Category[];
    },
    enabled: !!tenantId,
  });

  // Buscar formulários para o select
  const { data: forms } = useQuery({
    queryKey: ["forms-for-categories", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forms")
        .select("id, nome")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo")
        .order("nome", { ascending: true });
      
      if (error) {
        console.error("Erro ao buscar formulários:", error);
        throw error;
      }
      return data || [];
    },
    enabled: !!tenantId,
  });

  const uploadPhoto = async (file: File, categoryId: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${categoryId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('category-photos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('category-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const deletePhoto = async (url: string) => {
    if (!url) return;
    const path = url.split('/category-photos/')[1];
    if (path) {
      await supabase.storage.from('category-photos').remove([path]);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");
      
      // Obter maior ordem atual
      const { data: maxOrderData } = await supabase
        .from("product_categories")
        .select("ordem")
        .eq("tenant_id", tenantId)
        .order("ordem", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      const nextOrder = (maxOrderData?.ordem ?? -1) + 1;

      const formIdValue = selectedFormId === "__none__" ? null : selectedFormId;

      const categoryData = {
        nome: formData.get("nome") as string,
        descricao: (formData.get("descricao") as string) || null,
        form_id: formIdValue || null,
        cor_hex: (formData.get("cor_hex") as string) || null,
        status: (formData.get("status") as string) || "ativo",
        icone: selectedIcon || null,
        ordem: nextOrder,
        foto_url: null as string | null,
        tenant_id: tenantId,
      };

      const { data, error } = await supabase
        .from("product_categories")
        .insert(categoryData)
        .select()
        .single();

      if (error) throw error;

      if (photoFile && data) {
        const photoUrl = await uploadPhoto(photoFile, data.id);
        const { error: updateError } = await supabase
          .from("product_categories")
          .update({ foto_url: photoUrl })
          .eq("id", data.id);
        
        if (updateError) throw updateError;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product_categories"] });
      toast.success("Categoria criada com sucesso");
      const name = variables.get("nome") as string;
      if (data?.id) {
        void logCrud({ entitySlug: "entity:product_categories", operationSlug: "create", entityId: data.id, entityName: name, screenSlug: "categorias" });
      }
      setOpen(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedIcon("");
      setSelectedColor("#000000");
      setSelectedFormId("__none__");
    },
    onError: () => toast.error("Erro ao criar categoria"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      let photoUrl = editingCategory?.foto_url;

      if (photoFile) {
        if (editingCategory?.foto_url) {
          await deletePhoto(editingCategory.foto_url);
        }
        photoUrl = await uploadPhoto(photoFile, id);
      }

      const formIdValue = selectedFormId === "__none__" ? null : selectedFormId;

      const { error } = await supabase
        .from("product_categories")
        .update({
          nome: formData.get("nome") as string,
          descricao: (formData.get("descricao") as string) || null,
          form_id: formIdValue || null,
          cor_hex: (formData.get("cor_hex") as string) || null,
          status: (formData.get("status") as string) || "ativo",
          icone: selectedIcon || null,
          foto_url: photoUrl,
        })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product_categories"] });
      toast.success("Categoria atualizada com sucesso");
      void logCrud({ entitySlug: "entity:product_categories", operationSlug: "update", entityId: (variables as any).id, screenSlug: "categorias", before: (editingCategory as any), after: (variables as any) });
      setOpen(false);
      setEditingCategory(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedIcon("");
      setSelectedColor("#000000");
      setSelectedFormId("__none__");
    },
    onError: () => toast.error("Erro ao atualizar categoria"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const category = categories?.find((c) => c.id === id);
      const { error } = await supabase.from("product_categories").delete().eq("id", id);
      if (error) throw error;
      if (category?.foto_url) {
        await deletePhoto(category.foto_url);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_categories"] });
      toast.success("Categoria excluída com sucesso");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:product_categories", operationSlug: "delete", entityId: deleteId, screenSlug: "categorias" });
      }
      setDeleteId(null);
    },
    onError: (error: any) => {
      const isFK = error?.code === "23503" || error?.message?.includes("foreign key constraint") || error?.status === 409;
      if (isFK) {
        toast.error(
          "Não é possível excluir esta categoria porque existem registros relacionados. Remova ou ajuste os vínculos antes de tentar novamente."
        );
      } else {
        toast.error("Erro ao excluir categoria");
      }
    },
  });

  // Mutation para alternar status
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, newStatus }: { id: string; newStatus: string }) => {
      const { error } = await supabase
        .from("product_categories")
        .update({ status: newStatus })
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_categories", tenantId] });
      toast.success("Status alterado com sucesso");
      if (editingCategory) {
        void logCrud({ entitySlug: "entity:product_categories", operationSlug: "update", entityId: editingCategory.id, entityName: editingCategory.nome, screenSlug: "categorias" });
      }
    },
    onError: () => toast.error("Erro ao alterar status"),
  });

  // Mutation para atualizar ordem
  const updateOrderMutation = useMutation({
    mutationFn: async (reorderedCategories: Category[]) => {
      const updates = reorderedCategories.map((category, index) => {
        return supabase
          .from("product_categories")
          .update({ ordem: index })
          .eq("id", category.id);
      });

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product_categories"] });
      toast.success("Ordem das categorias atualizada");
    },
    onError: () => {
      toast.error("Erro ao atualizar ordem");
    },
  });

  // Handler para drag and drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && categories) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedCategories = arrayMove(categories, oldIndex, newIndex);
        updateOrderMutation.mutate(reorderedCategories);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setPhotoPreview(category.foto_url);
      setSelectedIcon(category.icone || "");
      setSelectedColor(category.cor_hex || "#000000");
      setSelectedFormId(category.form_id || "__none__");
    } else {
      setEditingCategory(null);
      setPhotoPreview(null);
      setSelectedIcon("");
      setSelectedColor("#000000");
      setSelectedFormId("__none__");
    }
    setPhotoFile(null);
    setOpen(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // Renderizar ícone dinamicamente
  const renderIcon = (iconName: string | null) => {
    if (!iconName) return null;
    const toPascalCase = (str: string) => {
      return str
        .split(/[-_\s]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join("");
    };
    const pascalName = toPascalCase(iconName);
    const IconComponent = (Icons as Record<string, any>)[pascalName];
    return IconComponent ? <IconComponent className="h-4 w-4" /> : null;
  };

  const matchesFilters = (category: Category) => {
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        category.nome?.toLowerCase().includes(searchLower) ||
        category.descricao?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    if (filters.status.length > 0) {
      if (!filters.status.includes(category.status)) return false;
    }

    return true;
  };

  const filteredCategories = useMemo(() => {
    if (!categories) return [] as Category[];
    return categories.filter((category) => matchesFilters(category));
  }, [categories, filters]);

  const paginatedCategories = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCategories.slice(start, start + itemsPerPage);
  }, [filteredCategories, currentPage, itemsPerPage]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    categories?.forEach((c) => {
      if (c.status) statuses.add(c.status);
    });
    return Array.from(statuses).sort();
  }, [categories]);

  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
    };

    if (!categories) return counts;

    categories.forEach((category) => {
      // Verificar se a categoria corresponde aos outros filtros (exceto o que está sendo contado)
      const matchesOtherFilters = (exceptFilter?: "status") => {
        // Filtro por busca
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            category.nome?.toLowerCase().includes(searchLower) ||
            category.descricao?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        // Filtro por status (ignorar se estiver contando status)
        if (exceptFilter !== "status" && filters.status.length > 0) {
          if (!filters.status.includes(category.status)) return false;
        }

        return true;
      };

      // Contar status (ignorando filtro de status)
      if (category.status && matchesOtherFilters("status")) {
        counts.status[category.status] = (counts.status[category.status] || 0) + 1;
      }
    });

    return counts;
  }, [categories, filters]);

  const handleFiltersChange = (newFilters: CategoryFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    filters.status.forEach((s) => {
      chips.push({
        id: `status:${s}`,
        label: `Status: ${s}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, status: filters.status.filter((x) => x !== s) });
        },
      });
    });
    
    return chips;
  }, [filters]);

  return (
    <div className="space-y-6" data-cy="categories-page">
      <div>
        <h1 className="text-3xl font-bold" data-cy="categories-title">Categorias do Sistema</h1>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="products" data-cy="product-categories-tab">
            <Package className="h-4 w-4 mr-2" />
            Categorias de Produtos
          </TabsTrigger>
          <TabsTrigger value="expenses" data-cy="expense-categories-tab">
            <Receipt className="h-4 w-4 mr-2" />
            Categorias de Despesas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-2xl font-semibold">Categorias de Produtos</h2>
              <p className="text-muted-foreground">Gerencie as categorias do seu catálogo</p>
            </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto" onClick={() => handleOpenDialog()} data-cy="new-category-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nova Categoria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl p-0 overflow-hidden">
            <div className="flex max-h-[90vh] flex-col">
              <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
                <DialogTitle>{editingCategory ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
                <DialogDescription>
                  Preencha os dados da categoria
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-32">
                  <Label htmlFor="foto">Foto da Categoria</Label>
                  {photoPreview ? (
                    <div
                      className="relative cursor-pointer mt-2"
                      onClick={() => fileInputRef.current?.click()}
                      title="Selecionar outra imagem"
                    >
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto();
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="mt-2 w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-muted/50"
                      onClick={() => fileInputRef.current?.click()}
                      title="Selecionar imagem"
                    >
                      <Upload className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    id="foto"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    aria-hidden="true"
                  />
                </div>

                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome da Categoria *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      defaultValue={editingCategory?.nome}
                      required
                      data-cy="category-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cor_hex">Cor da Categoria</Label>
                      <div className="flex gap-2">
                        <Input
                          id="cor_hex"
                          name="cor_hex"
                          type="color"
                          value={selectedColor}
                          onChange={(e) => {
                            setSelectedColor(e.target.value);
                            const hiddenInput = document.getElementById("cor_hex_hidden") as HTMLInputElement;
                            if (hiddenInput) {
                              hiddenInput.value = e.target.value;
                            }
                          }}
                          className="w-20 h-10"
                          data-cy="category-color-input"
                        />
                        <Input
                          type="text"
                          value={selectedColor}
                          onChange={(e) => {
                            setSelectedColor(e.target.value);
                            const hiddenInput = document.getElementById("cor_hex_hidden") as HTMLInputElement;
                            if (hiddenInput) {
                              hiddenInput.value = e.target.value;
                            }
                          }}
                          placeholder="#000000"
                          className="flex-1"
                        />
                        <input
                          type="hidden"
                          id="cor_hex_hidden"
                          name="cor_hex"
                          value={selectedColor}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select name="status" defaultValue={editingCategory?.status || "ativo"}>
                        <SelectTrigger data-cy="category-status-select">
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ativo">Ativo</SelectItem>
                          <SelectItem value="inativo">Inativo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Ícone da Categoria</Label>
                    <IconPicker
                      value={selectedIcon}
                      onValueChange={(iconName) => {
                        setSelectedIcon(iconName);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição da Categoria</Label>
                <Textarea
                  id="descricao"
                  name="descricao"
                  defaultValue={editingCategory?.descricao || ""}
                  rows={3}
                  data-cy="category-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="form_id">Formulário da Categoria</Label>
                <Select
                  value={selectedFormId}
                  onValueChange={setSelectedFormId}
                >
                  <SelectTrigger data-cy="category-form-select">
                    <SelectValue placeholder="Nenhum formulário" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum formulário</SelectItem>
                    {forms && forms.length > 0 ? (
                      forms.map((form) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.nome}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="__no_forms__" disabled>
                        Nenhum formulário cadastrado
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <input
                  type="hidden"
                  name="form_id"
                  value={selectedFormId === "__none__" ? "" : selectedFormId}
                />
              </div>
                </form>
              </div>
              <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" form="category-form" data-cy="category-submit-btn">
                  {editingCategory ? "Atualizar" : "Criar"} Categoria
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card data-cy="categories-table-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="categories-table-title">Lista de Categorias</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="categories-search-input"
                  />
                </div>
                <CategoryFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  statuses={allStatuses}
                  counts={facetCounts}
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8" data-cy="categories-loading">Carregando...</div>
          ) : filteredCategories && filteredCategories.length > 0 ? (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={categories?.map((c) => c.id) || []}
                  strategy={verticalListSortingStrategy}
                >
                  <Table data-cy="categories-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Foto</TableHead>
                        <TableHead>Nome / Ícone / Cor</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead>Formulário</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedCategories.map((category) => (
                        <SortableCategoryRow
                          key={category.id}
                          category={category}
                          onEdit={() => handleOpenDialog(category)}
                          onDelete={() => setDeleteId(category.id)}
                          onView={() => setCategoryProfile({ id: category.id, name: category.nome })}
                          renderIcon={renderIcon}
                          onToggleStatus={(id, newStatus) => toggleStatusMutation.mutate({ id, newStatus })}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </SortableContext>
              </DndContext>

              <TablePagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredCategories.length / itemsPerPage)}
                totalItems={filteredCategories.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
                data-cy="categories-pagination"
              />
            </>
          ) : categories && categories.length > 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="categories-empty-filtered">
              Nenhuma categoria encontrada com os filtros aplicados.
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-cy="categories-empty">
              Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {categoryProfile && (() => {
        const cat = (categories || []).find((x: any) => x.id === categoryProfile.id);
        const photo = cat?.foto_url || null;
        const infoItems = [
          cat?.descricao ? { value: cat.descricao } : null,
          cat?.created_at ? { value: `Criada em ${new Intl.DateTimeFormat('pt-BR').format(new Date(cat.created_at))}` } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!categoryProfile}
            onOpenChange={() => setCategoryProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"CATEGORIA DE PRODUTO"}
            entityName={categoryProfile.name}
            infoItems={infoItems}
            contentClassName="px-2 pb-2 pt-1"
            renderContent={({ periodRange, setLoading }) => (
              <ProductCategoryDetails id={categoryProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
        </TabsContent>

        <TabsContent value="expenses" className="space-y-6">
          <ExpenseCategoriesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface SortableCategoryRowProps {
  category: Category;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
  renderIcon: (iconName: string | null) => React.ReactNode;
  onToggleStatus: (id: string, newStatus: string) => void;
}

function SortableCategoryRow({
  category,
  onEdit,
  onDelete,
  onView,
  renderIcon,
  onToggleStatus,
}: SortableCategoryRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} data-cy={`category-row-${category.id}`}>
      <TableCell>
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </TableCell>
      <TableCell>
        {category.foto_url ? (
          <img
            src={category.foto_url}
            alt={category.nome}
            className="w-10 h-10 object-cover rounded"
          />
        ) : (
          <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
            <Upload className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {category.cor_hex && (
            <div
              className="w-4 h-4 rounded border"
              style={{ backgroundColor: category.cor_hex }}
            />
          )}
          {renderIcon(category.icone)}
          <span>{category.nome}</span>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {category.descricao || "—"}
      </TableCell>
      <TableCell>
        {category.forms?.nome || "—"}
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const newStatus = category.status === "ativo" ? "inativo" : "ativo";
                  onToggleStatus(category.id, newStatus);
                }}
                className="cursor-pointer"
              >
                <Badge 
                  variant={category.status === "ativo" ? "default" : "secondary"}
                  className={
                    category.status === "ativo" 
                      ? "bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer" 
                      : category.status === "inativo" 
                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer" 
                      : ""
                  }
                >
                  {category.status}
                </Badge>
              </button>
            </TooltipTrigger>
            <TooltipContent className="whitespace-pre-line">
              {category.status === "ativo" 
                ? "Clique para DESATIVAR" 
                : "Clique para ATIVAR"}
              {'\n'}a categoria <strong>{category.nome}</strong>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      <TableCell className="text-right">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="icon-chip-blue"
                onClick={onView}
                data-cy={`category-view-btn-${category.id}`}
              >
                <FileText className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="whitespace-pre-line">
              Ver ficha completa e relatórios{'\n'}da categoria <strong>{category.nome}</strong>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="icon-chip-green"
                onClick={onEdit}
                data-cy={`category-edit-btn-${category.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-center">
                <div>Editar categoria</div>
                <div className="font-bold">{category.nome}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="icon-chip-red"
                onClick={onDelete}
                data-cy={`category-delete-btn-${category.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="text-center">
                <div>Excluir categoria</div>
                <div className="font-bold">{category.nome}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}

